import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import {
  Database,
  Building,
  MapPin,
  Users,
  FileText,
  Settings,
  Upload,
  Download,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  TrendingUp,
  Activity,
  Target,
  Calendar
} from 'lucide-react';

// Import your actual types and services
import {  Contractor } from '../../types/master-data.types';
import { masterDataAPI } from '../../services/master-data-api.service';
import { worksApiService } from '../../services/workApi.service';
import { District, Mandal } from '@/types/territorial.types';

interface AdministrativeSanction {
  id: string;
  workId: string;
  level: 'STATE' | 'DISTRICT' | 'MANDAL' | 'GP';
  grantType: 'CRR' | 'PMGSY' | 'MRR' | 'FDR' | 'NABARD';
  financialYear: string;
  sanctionDate: string;
  authority: string;
  proceedingsNumber: string;
  totalWorks: number;
  totalLength: number;
  totalAmount: number;
  districts?: number[];
  mandals?: number[];
  status: 'ACTIVE' | 'REVISED' | 'DROPPED' | 'ALTERNATE';
}

interface MasterDataStats {
  totalDistricts: number;
  totalMandals: number;
  totalContractors: number;
  totalWorks: number;
  activeWorks: number;
  completedWorks: number;
  totalSanctionAmount: number;
  pendingApprovals: number;
}

interface DistrictMandalHierarchy {
  district_id: number;
  district_name: string;
  district_code: string;
  mandal_count: number;
  works_count: number;
  total_sanction_amount: number;
  mandals: Array<{
    mandal_id: number;
    mandal_name: string;
    mandal_code: string;
    mandal_type: 'URBAN' | 'RURAL';
    works_count: number;
    sanction_amount: number;
  }>;
}

const MasterDataDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'districts' | 'mandals' | 'sanctions' | 'contractors'>('overview');
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState<string>('ALL');

  // State for master data
  const [stats, setStats] = useState<MasterDataStats | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [mandals, setMandals] = useState<Mandal[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [hierarchy, setHierarchy] = useState<DistrictMandalHierarchy[]>([]);
  const [sanctions, setSanctions] = useState<AdministrativeSanction[]>([]);

  // Fetch master data
  useEffect(() => {
    const fetchMasterData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all master data
        const [
          districtsData,
          mandalsData, 
          contractorsData,
          worksData,
          masterStats
        ] = await Promise.all([
          masterDataAPI.getDistricts(),
          masterDataAPI.getMandals(),
          masterDataAPI.getContractors(),
          worksApiService.getAllWorks({ limit: 10000 }),
          masterDataAPI.getMasterDataStats()
        ]);

        setDistricts(districtsData);
        setMandals(mandalsData);
        setContractors(contractorsData);

        // Calculate comprehensive stats
        const calculatedStats: MasterDataStats = {
          totalDistricts: districtsData.length,
          totalMandals: mandalsData.length,
          totalContractors: contractorsData.filter(c => c.is_active).length,
          totalWorks: worksData.works.length,
          activeWorks: worksData.works.filter(w => w.is_active).length,
          completedWorks: worksData.works.filter(w => w.abstract_stage_of_work === 'Completed').length,
          totalSanctionAmount: worksData.works.reduce((sum, w) => sum + (w.ras_amount || w.estimate_cost), 0),
          pendingApprovals: worksData.works.filter(w => w.abstract_stage_of_work === 'Not Started').length
        };
        setStats(calculatedStats);


        // Build district-mandal hierarchy
        const hierarchyData = buildDistrictMandalHierarchy(districtsData, mandalsData, worksData.works);
        setHierarchy(hierarchyData);

        // Generate sample administrative sanctions (in real app, this would come from API)
        const sanctionsData = generateSampleSanctions(worksData.works);
        setSanctions(sanctionsData);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch master data');
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  // Build district-mandal hierarchy with work statistics
  const buildDistrictMandalHierarchy = (districts: District[], mandals: Mandal[], works: any[]): DistrictMandalHierarchy[] => {
    return districts.map(district => {
      const districtMandals = mandals.filter(m => m.id === district.id);
      const districtWorks = works.filter(w => w.district_id === district.id);
      
      const hierarchyItem: DistrictMandalHierarchy = {
        district_id: district.id,
        district_name: district.name,
        district_code: district.code,
        mandal_count: districtMandals.length,
        works_count: districtWorks.length,
        total_sanction_amount: districtWorks.reduce((sum, w) => sum + (w.ras_amount || w.estimate_cost), 0),
        mandals: districtMandals.map(mandal => {
          const mandalWorks = works.filter(w => w.mandal_id === mandal.id);
          return {
            mandal_id: mandal.id,
            mandal_name: mandal.name,
            mandal_code: mandal.code,
            mandal_type: mandal.mandal_type as 'URBAN' | 'RURAL',
            works_count: mandalWorks.length,
            sanction_amount: mandalWorks.reduce((sum, w) => sum + (w.ras_amount || w.estimate_cost), 0)
          };
        })
      };

      return hierarchyItem;
    });
  };

  // Generate sample administrative sanctions
  const generateSampleSanctions = (works: any[]): AdministrativeSanction[] => {
    const grantTypes = ['CRR', 'PMGSY', 'MRR', 'FDR', 'NABARD'];
    const levels = ['STATE', 'DISTRICT', 'MANDAL', 'GP'];
    const authorities = [
      'Government Of Telangana',
      'Dist.Collector',
      'CEO,ZP',
      'Joint Director',
      'Project Director, DRDA',
      'MPDO'
    ];

    // Group works by similar characteristics to create logical sanctions
    const sanctionsMap = new Map<string, AdministrativeSanction>();
    
    works.forEach((work, index) => {
      const key = `${work.oas_grant_level}-${work.grant_type}-${work.district_id}`;
      
      if (!sanctionsMap.has(key)) {
        sanctionsMap.set(key, {
          id: `SANC_${index + 1}`,
          workId: `WID_${index + 1}`,
          level: levels[Math.floor(Math.random() * levels.length)] as any,
          grantType: (work.grant_type || grantTypes[Math.floor(Math.random() * grantTypes.length)]) as any,
          financialYear: new Date(work.oas_sanction_date || new Date()).getFullYear().toString(),
          sanctionDate: work.oas_sanction_date || new Date().toISOString().split('T')[0],
          authority: authorities[Math.floor(Math.random() * authorities.length)],
          proceedingsNumber: work.oas_proceedings_number || `GoRT.No ${300 + index}`,
          totalWorks: 1,
          totalLength: work.length_sanctioned_km || 0,
          totalAmount: (work.ras_amount || work.estimate_cost) / 100000, // Convert to lakhs
          districts: [work.district_id],
          mandals: [work.mandal_id],
          status: 'ACTIVE' as any
        });
      } else {
        const existing = sanctionsMap.get(key)!;
        existing.totalWorks += 1;
        existing.totalLength += work.length_sanctioned_km || 0;
        existing.totalAmount += (work.ras_amount || work.estimate_cost) / 100000;
        if (work.mandal_id && !existing.mandals?.includes(work.mandal_id)) {
          existing.mandals?.push(work.mandal_id);
        }
      }
    });

    return Array.from(sanctionsMap.values()).slice(0, 50); // Limit to 50 for display
  };

  // Filtered data based on search and filters
  const filteredHierarchy = useMemo(() => {
    return hierarchy.filter(district => {
      const matchesSearch = district.district_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           district.district_code.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [hierarchy, searchTerm]);

  const filteredSanctions = useMemo(() => {
    return sanctions.filter(sanction => {
      const matchesSearch = sanction.proceedingsNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           sanction.authority.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLevel = filterLevel === 'ALL' || sanction.level === filterLevel;
      return matchesSearch && matchesLevel;
    });
  }, [sanctions, searchTerm, filterLevel]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format large numbers
  const formatNumber = (num: number): string => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading master data dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Master Data Management</h1>
          <p className="text-gray-600 mt-1">Administrative structure and data management for PRED Telangana</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Districts</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalDistricts}</p>
              </div>
              <Building className="w-6 h-6 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Mandals</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalMandals}</p>
              </div>
              <MapPin className="w-6 h-6 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Contractors</p>
                <p className="text-2xl font-bold text-purple-600">{stats.totalContractors}</p>
              </div>
              <Users className="w-6 h-6 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Works</p>
                <p className="text-2xl font-bold text-orange-600">{stats.totalWorks}</p>
              </div>
              <FileText className="w-6 h-6 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Works</p>
                <p className="text-2xl font-bold text-indigo-600">{stats.activeWorks}</p>
              </div>
              <Activity className="w-6 h-6 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completedWorks}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sanctions</p>
                <p className="text-xl font-bold text-red-600">{formatNumber(stats.totalSanctionAmount)}</p>
              </div>
              <Target className="w-6 h-6 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendingApprovals}</p>
              </div>
              <Calendar className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'districts', label: 'Districts', icon: Building },
              { id: 'mandals', label: 'Mandals', icon: MapPin },
              { id: 'sanctions', label: 'Administrative Sanctions', icon: FileText },
              { id: 'contractors', label: 'Contractors', icon: Users }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {activeTab === 'sanctions' && (
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Levels</option>
                <option value="STATE">State Level</option>
                <option value="DISTRICT">District Level</option>
                <option value="MANDAL">Mandal Level</option>
                <option value="GP">GP Level</option>
              </select>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* District Distribution Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4">District-wise Work Distribution</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hierarchy.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="district_name" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip formatter={(value, name) => [value, name === 'works_count' ? 'Works' : 'Mandals']} />
                        <Legend />
                        <Bar dataKey="works_count" fill="#3B82F6" name="Works" />
                        <Bar dataKey="mandal_count" fill="#10B981" name="Mandals" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-4">Sanction Amount by District</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={hierarchy.slice(0, 8).map(d => ({
                            name: d.district_name,
                            value: d.total_sanction_amount / 10000000 // Convert to crores
                          }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value }) => `${name}: ₹${value.toFixed(1)}Cr`}
                        >
                          {hierarchy.slice(0, 8).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`₹${value} Cr`, 'Sanction Amount']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Key Insights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">Highest Work Density</span>
                  </div>
                  <p className="text-lg font-bold text-blue-900 mt-1">
                    {hierarchy.length > 0 ? 
                      hierarchy.reduce((prev, current) => prev.works_count > current.works_count ? prev : current).district_name 
                      : 'N/A'
                    }
                  </p>
                  <p className="text-sm text-blue-600">
                    {hierarchy.length > 0 ? 
                      `${hierarchy.reduce((prev, current) => prev.works_count > current.works_count ? prev : current).works_count} works` 
                      : ''
                    }
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">Largest District</span>
                  </div>
                  <p className="text-lg font-bold text-green-900 mt-1">
                    {hierarchy.length > 0 ? 
                      hierarchy.reduce((prev, current) => prev.mandal_count > current.mandal_count ? prev : current).district_name 
                      : 'N/A'
                    }
                  </p>
                  <p className="text-sm text-green-600">
                    {hierarchy.length > 0 ? 
                      `${hierarchy.reduce((prev, current) => prev.mandal_count > current.mandal_count ? prev : current).mandal_count} mandals` 
                      : ''
                    }
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-800">Average per District</span>
                  </div>
                  <p className="text-lg font-bold text-purple-900 mt-1">
                    {hierarchy.length > 0 ? Math.round(hierarchy.reduce((sum, d) => sum + d.works_count, 0) / hierarchy.length) : 0} works
                  </p>
                  <p className="text-sm text-purple-600">
                    {hierarchy.length > 0 ? Math.round(hierarchy.reduce((sum, d) => sum + d.mandal_count, 0) / hierarchy.length) : 0} mandals avg
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Districts Tab */}
          {activeTab === 'districts' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Districts Management</h3>
                <button className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Add District
                </button>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandals</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Works</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sanction Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredHierarchy.map((district) => (
                      <tr key={district.district_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{district.district_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{district.district_code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{district.mandal_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{district.works_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(district.total_sanction_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button className="text-blue-600 hover:text-blue-900">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-green-600 hover:text-green-900">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-900">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Administrative Sanctions Tab */}
          {activeTab === 'sanctions' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Administrative Sanctions Management</h3>
                <button className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Sanction
                </button>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proceedings No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grant Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authority</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Works</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount (₹ Lakhs)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSanctions.slice(0, 20).map((sanction) => (
                      <tr key={sanction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sanction.proceedingsNumber}</div>
                          <div className="text-sm text-gray-500">{sanction.sanctionDate}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            sanction.level === 'STATE' ? 'bg-purple-100 text-purple-800' :
                            sanction.level === 'DISTRICT' ? 'bg-blue-100 text-blue-800' :
                            sanction.level === 'MANDAL' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {sanction.level}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sanction.grantType}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{sanction.authority}</div>
                          <div className="text-sm text-gray-500">FY {sanction.financialYear}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{sanction.totalWorks} works</div>
                          {sanction.totalLength > 0 && (
                            <div className="text-xs text-gray-500">{sanction.totalLength.toFixed(1)} KM</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{sanction.totalAmount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            sanction.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                            sanction.status === 'REVISED' ? 'bg-yellow-100 text-yellow-800' :
                            sanction.status === 'DROPPED' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {sanction.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button className="text-blue-600 hover:text-blue-900" title="View Details">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-green-600 hover:text-green-900" title="Edit">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-purple-600 hover:text-purple-900" title="Upload Document">
                              <Upload className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-900" title="Drop Sanction">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sanctions Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-800">State Level</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {sanctions.filter(s => s.level === 'STATE').length}
                  </p>
                  <p className="text-sm text-purple-600">
                    ₹{sanctions.filter(s => s.level === 'STATE').reduce((sum, s) => sum + s.totalAmount, 0).toFixed(2)}L
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Building className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">District Level</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {sanctions.filter(s => s.level === 'DISTRICT').length}
                  </p>
                  <p className="text-sm text-blue-600">
                    ₹{sanctions.filter(s => s.level === 'DISTRICT').reduce((sum, s) => sum + s.totalAmount, 0).toFixed(2)}L
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">Mandal Level</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {sanctions.filter(s => s.level === 'MANDAL').length}
                  </p>
                  <p className="text-sm text-green-600">
                    ₹{sanctions.filter(s => s.level === 'MANDAL').reduce((sum, s) => sum + s.totalAmount, 0).toFixed(2)}L
                  </p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-orange-600 mr-2" />
                    <span className="text-sm font-medium text-orange-800">GP Level</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-900 mt-1">
                    {sanctions.filter(s => s.level === 'GP').length}
                  </p>
                  <p className="text-sm text-orange-600">
                    ₹{sanctions.filter(s => s.level === 'GP').reduce((sum, s) => sum + s.totalAmount, 0).toFixed(2)}L
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mandals Tab */}
          {activeTab === 'mandals' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Mandals Management</h3>
                <div className="flex space-x-2">
                  <select
                    value={selectedDistrict || ''}
                    onChange={(e) => setSelectedDistrict(Number(e.target.value) || null)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Districts</option>
                    {districts.map(district => (
                      <option key={district.id} value={district.id}>
                        {district.name}
                      </option>
                    ))}
                  </select>
                  <button className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Mandal
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Works</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sanction Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredHierarchy
                      .filter(district => !selectedDistrict || district.district_id === selectedDistrict)
                      .flatMap(district => 
                        district.mandals
                          .filter(mandal => 
                            mandal.mandal_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            mandal.mandal_code.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map(mandal => ({ ...mandal, district_name: district.district_name }))
                      )
                      .slice(0, 50)
                      .map((mandal) => (
                        <tr key={mandal.mandal_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">{mandal.mandal_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mandal.district_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mandal.mandal_code}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              mandal.mandal_type === 'URBAN' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {mandal.mandal_type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mandal.works_count}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(mandal.sanction_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-900">
                                <Eye className="w-4 h-4" />
                              </button>
                              <button className="text-green-600 hover:text-green-900">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button className="text-red-600 hover:text-red-900">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Mandal Type Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold mb-4">Mandal Type Distribution</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { 
                              name: 'Urban', 
                              value: mandals.filter(m => m.mandal_type === 'URBAN').length,
                              fill: '#8B5CF6'
                            },
                            { 
                              name: 'Rural', 
                              value: mandals.filter(m => m.mandal_type === 'RURAL').length,
                              fill: '#10B981'
                            }
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                        >
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-md font-semibold mb-4">Top 10 Mandals by Works</h4>
                  <div className="space-y-2">
                    {filteredHierarchy
                      .flatMap(district => district.mandals.map(mandal => ({ ...mandal, district_name: district.district_name })))
                      .sort((a, b) => b.works_count - a.works_count)
                      .slice(0, 10)
                      .map((mandal, index) => (
                        <div key={mandal.mandal_id} className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-600 w-6">{index + 1}.</span>
                            <span className="text-sm text-gray-900">{mandal.mandal_name}</span>
                            <span className="text-xs text-gray-500 ml-2">({mandal.district_name})</span>
                          </div>
                          <span className="text-sm font-semibold text-blue-600">{mandal.works_count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contractors Tab */}
          {activeTab === 'contractors' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Contractors Management</h3>
                <button className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contractor
                </button>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contractor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firm Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PAN Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contractors
                      .filter(contractor => 
                        contractor.contractor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        contractor.firm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        contractor.pan_number.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .slice(0, 20)
                      .map((contractor) => (
                        <tr key={contractor.contractor_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">{contractor.contractor_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contractor.firm_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{contractor.pan_number}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contractor.contact_number || 'Not provided'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {contractor.bank_name || 'Not provided'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contractor.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {contractor.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button className="text-blue-600 hover:text-blue-900">
                                <Eye className="w-4 h-4" />
                              </button>
                              <button className="text-green-600 hover:text-green-900">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button className="text-red-600 hover:text-red-900">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Contractor Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-800">Active Contractors</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {contractors.filter(c => c.is_active).length}
                  </p>
                  <p className="text-sm text-green-600">
                    {((contractors.filter(c => c.is_active).length / contractors.length) * 100).toFixed(1)}% of total
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm font-medium text-red-800">Inactive Contractors</span>
                  </div>
                  <p className="text-2xl font-bold text-red-900 mt-1">
                    {contractors.filter(c => !c.is_active).length}
                  </p>
                  <p className="text-sm text-red-600">
                    {((contractors.filter(c => !c.is_active).length / contractors.length) * 100).toFixed(1)}% of total
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">Total Contractors</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {contractors.length}
                  </p>
                  <p className="text-sm text-blue-600">
                    Registered in system
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterDataDashboard;